import React, { useState, useEffect, useRef } from 'react';
import {
  LoadingOutlined,
  MinusCircleFilled,
  PlusCircleFilled,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';
import {
  GroupNodeContent,
  GroupNodeHeader,
  GroupNodeHeaderIcon,
  GroupNodeHeaderLeft,
  GroupNodeHeaderName,
  GroupNodeWrapper,
  DeviceNodeIcon,
  DeviceNodeName,
  DeviceNodeWrapper,
  DeviceNodeActions,
  DeviceNodeAction,
  GroupNodeHeaderActions,
  GroupNodeHeaderAction,
} from '../../Composable';
import CameraOnIcon from '../../../../assets/icons/camera_on.svg';
import CameraOffIcon from '../../../../assets/icons/camera_off.svg';
import { useLatestCameraState } from '../../../../hooks/use-latest-camera-state';

import './styles.scss';
import { Modal, Spin, Tooltip } from 'antd';
import {
  CreateGroup,
  defaultAllowActions,
  EditName,
  GroupNodeMore,
} from '../../Aggregated';
import {
  useRemoveCamera,
  useRemoveGroup,
} from '../../../../hooks/socket/cameras';
import { TrashCanIcon } from '../../../../icons/TrashCan';
import CameraSettingPopup from '../../../../pages/Camera/CameraSettingPopup';
import { useDispatch, useSelector } from 'react-redux';
import HistoryEventsModal from '../../../../pages/Camera/HistoryEventsModal';
import { ServicesModal } from '../../../../pages/Camera/ServicesModal';
import { SettingsIcon } from '../../../../icons/Settings';
import {
  updateRecoveryCameraPage,
  updateRecoveryHistoryEventOfCameraPage,
} from '../../../../redux/recovery/action';
import {
  getRecoveryCameraPage,
  getRecoveryHistoryEventOfCameraPage,
} from '../../../../redux/recovery/reducer';
import { beeXLogger } from '../../../../helpers/logger';

export const RootTree = ({
  namespace,
  onSelect,
  selectedId,
  rootGroups,
  getChildren,
  getCameras,
  groupMap,
  onSelectCamera,
  getIsExpanded,
  onExpand,
  onRenameSuccess,
  onAddGroupSuccess,
  onRemoveGroupSuccess,
  onRemoveCameraSuccess,
  onUpdateCamera,
  onUpdateStream,
  onAddCameraToGroup,
  getIsCameraSelected,
  getShouldScrollAfterLoad,
  getAllowModify = () => true,
  getIsCameraPinned = () => false,
  onPinCamera = () => {},
}) => {
  const onNodeSelect = (groupId) => {
    onSelect(groupMap.current[groupId]);
  };

  const isSelected = (groupId) => selectedId === groupId;

  return (
    <div id="camera-root-tree">
      <div className="root-group-list">
        {rootGroups?.map((group) => (
          <GroupNode
            key={group.id}
            id={group.id}
            name={group.name}
            namespace={namespace}
            type={group.type}
            getChildren={getChildren}
            getIsSelected={isSelected}
            isParentSelected={false}
            getIsExpanded={getIsExpanded}
            onSelect={onNodeSelect}
            onExpand={onExpand}
            level={0}
            getCameras={getCameras}
            onSelectCamera={onSelectCamera}
            onRenameSuccess={onRenameSuccess}
            onAddGroupSuccess={onAddGroupSuccess}
            onRemoveGroupSuccess={onRemoveGroupSuccess}
            onRemoveCameraSuccess={onRemoveCameraSuccess}
            onUpdateCamera={onUpdateCamera}
            onUpdateStream={onUpdateStream}
            onAddCameraToGroup={onAddCameraToGroup}
            getIsCameraSelected={getIsCameraSelected}
            getShouldScrollAfterLoad={getShouldScrollAfterLoad}
            getAllowModify={getAllowModify}
            getIsCameraPinned={getIsCameraPinned}
            onPinCamera={onPinCamera}
          />
        ))}
      </div>
    </div>
  );
};

export const GroupNode = ({
  name,
  id,
  getChildren,
  getIsSelected,
  onSelect,
  getIsExpanded,
  onExpand,
  level,
  getCameras,
  getCameraTooltip,
  getTooltip,
  onSelectCamera = () => {},
  getIsCameraSelected = () => false,
  namespace,
  type,
  onRenameSuccess,
  onAddGroupSuccess,
  onRemoveGroupSuccess,
  onRemoveCameraSuccess,
  onUpdateCamera,
  onUpdateStream,
  onAddCameraToGroup,
  getShouldScrollAfterLoad,
  getAllowModify = () => true,
  getIsCameraPinned = () => false,
  onPinCamera = () => {},
}) => {
  const [cameras, setCameras] = useState([]);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [children, setChildren] = useState(undefined);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const isExpanded = getIsExpanded(id);
  const isSelected = getIsSelected(id);

  const isLoading = isLoadingChildren || isLoadingCameras;
  const [isAddingSubgroup, setIsAddingSubgroup] = useState(false);
  const { removeGroup } = useRemoveGroup();
  const { removeCamera } = useRemoveCamera();
  const ref = useRef(null);

  const allowModify = getAllowModify(id);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    setIsLoadingChildren(true);
    getChildren(id)
      .then(setChildren)
      .finally(() => setIsLoadingChildren(false));
    setIsLoadingCameras(true);
    getCameras(id)
      .then((res) => {
        console.log('🚀 ~ GroupNode ~ res:', id, name, res);
        setCameras(res);
      })
      .finally(() => setIsLoadingCameras(false));
  }, [id, getChildren, isExpanded, getCameras]);

  useEffect(() => {
    if (getShouldScrollAfterLoad?.(id)) {
      console.log('scroll to', id, ref.current);
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    getShouldScrollAfterLoad,
    isExpanded,
    children !== undefined,
    cameras !== undefined,
    isLoadingChildren,
    isLoadingCameras,
    id,
  ]);

  const doRemoveGroup = () => {
    return removeGroup(id, namespace).then(() => {
      onRemoveGroupSuccess(id);
    });
  };

  const onRemoveSelf = () => {
    Modal.confirm({
      title: 'Remove group',
      content: 'Are you sure you want to remove this group?',
      onOk: doRemoveGroup,
    });
  };

  const doRemoveCamera = (serial) => {
    return removeCamera(serial, id, namespace).then((res) => {
      console.log('🚀 ~ REMOVE_DEVICE_GROUP_MEMBER ~ res:', res);
      onRemoveCameraSuccess(id, serial);
    });
  };

  const onRemoveCamera = (serial) => {
    Modal.confirm({
      title: 'Remove camera',
      content: 'Are you sure you want to remove this camera?',
      onOk: () => doRemoveCamera(serial),
    });
  };

  const onAction = (action) => {
    switch (action) {
      case 'editName':
        setIsRenaming(true);
        break;
      case 'addSubgroup':
        setIsAddingSubgroup(true);
        break;
      case 'remove':
        onRemoveSelf();
        break;
      case 'addCamera':
        onAddCameraToGroup({
          id,
          name,
        });
        break;
      default:
        break;
    }
  };

  const renderChildren = () => {
    if (!isExpanded && !isAddingSubgroup) {
      return null;
    }

    return (
      <GroupNodeContent>
        {isAddingSubgroup && (
          <CreateGroup
            parentGroupId={id}
            namespace={namespace}
            level={level + 1}
            onCancel={() => setIsAddingSubgroup(false)}
            onSuccess={(newGroup) => {
              setIsAddingSubgroup(false);
              onAddGroupSuccess(newGroup, { id, name });
            }}
          />
        )}
        {children?.map((child) => (
          <GroupNode
            key={child.id}
            name={child.name}
            id={child.id}
            getChildren={getChildren}
            getIsSelected={getIsSelected}
            getIsExpanded={getIsExpanded}
            onSelect={onSelect}
            onExpand={onExpand}
            level={level + 1}
            getCameras={getCameras}
            getTooltip={getTooltip}
            getCameraTooltip={getCameraTooltip}
            onSelectCamera={onSelectCamera}
            getIsCameraSelected={getIsCameraSelected}
            namespace={namespace}
            type={child.type}
            onRenameSuccess={onRenameSuccess}
            onAddGroupSuccess={onAddGroupSuccess}
            onRemoveGroupSuccess={onRemoveGroupSuccess}
            onRemoveCameraSuccess={onRemoveCameraSuccess}
            onUpdateCamera={onUpdateCamera}
            onUpdateStream={onUpdateStream}
            onAddCameraToGroup={onAddCameraToGroup}
            getShouldScrollAfterLoad={getShouldScrollAfterLoad}
            getAllowModify={getAllowModify}
            getIsCameraPinned={getIsCameraPinned}
            onPinCamera={onPinCamera}
          />
        ))}
        <div
          className="group-node-cameras-container"
          style={{ maxHeight: 36 * 10, overflowY: 'auto' }}
        >
          {cameras?.map((cam) => (
            <WithLatestStateDeviceNode
              key={cam.serial}
              level={level + 1}
              state={cam.state}
              name={cam.name}
              tooltip={getCameraTooltip ? getCameraTooltip(cam) : cam.name}
              onSelect={() => onSelectCamera(cam, id)}
              isSelected={getIsCameraSelected(cam.serial)}
              serial={cam.serial}
              allowRemove={allowModify}
              allowEdit={allowModify}
              onRemove={() => onRemoveCamera(cam.serial)}
              removeTooltip="Remove from group"
              onUpdate={() => onUpdateCamera(cam)}
              onUpdateStream={() => onUpdateStream(cam)}
              camera={allowModify ? cam : undefined}
              pinned={getIsCameraPinned(cam.serial)}
              onPin={(pinned) => onPinCamera(cam, pinned)}
            />
          ))}
        </div>
      </GroupNodeContent>
    );
  };

  return (
    <GroupNodeWrapper
      level={level}
      isExpanded={isExpanded}
      isSelected={isSelected}
      id={`camera-group-${id}`}
      ref={ref}
    >
      <GroupNodeHeader>
        <GroupNodeHeaderLeft
          onClick={() => {
            if (isSelected || !isExpanded) {
              onExpand(id);
            }
            onSelect(id);
          }}
        >
          <GroupNodeHeaderIcon>
            {isExpanded ? <MinusCircleFilled /> : <PlusCircleFilled />}
          </GroupNodeHeaderIcon>
          {isRenaming ? (
            <EditName
              defaultName={name}
              onCancel={() => setIsRenaming(false)}
              groupId={id}
              namespace={namespace}
              groupType={type}
              onSuccess={(groupId, name) => {
                setIsRenaming(false);
                onRenameSuccess(groupId, name);
              }}
            />
          ) : (
            <GroupNodeHeaderName
              tooltip={getTooltip ? getTooltip({ name, id }) : undefined}
            >
              {name}
            </GroupNodeHeaderName>
          )}
        </GroupNodeHeaderLeft>
        <GroupNodeHeaderActions>
          {isLoading && (
            <GroupNodeHeaderAction>
              <Spin size="small" indicator={<LoadingOutlined spin />} />
            </GroupNodeHeaderAction>
          )}
          <GroupNodeMore
            actions={allowModify ? defaultAllowActions : {}}
            onAction={onAction}
          />
        </GroupNodeHeaderActions>
      </GroupNodeHeader>
      {renderChildren()}
    </GroupNodeWrapper>
  );
};

export const DeviceNode = ({
  level,
  state,
  name,
  tooltip,
  onSelect,
  isSelected,
  allowRemove,
  onRemove,
  removeTooltip,
  onUpdate,
  onUpdateStream,
  camera,
  variant = 'group',
  pinned,
  onPin,
}) => {
  const owner = useSelector((state) => state.userId);
  const [visibleDetail, setVisibleDetail] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [dataCameraEvent, setDataCameraEvent] = useState();
  const [isModalVisibleService, setIsModalVisibleService] = useState(false);
  const [dataServiceCamera, setDataServiceCamera] = useState();
  const dispatch = useDispatch();
  const {
    visibleCameraDetail: recoveryVisibleCameraDetail,
    cameraToService: recoveryCameraToService,
  } = useSelector(getRecoveryCameraPage);
  const { camera: recoveryCameraToHistoryEvent } = useSelector(
    getRecoveryHistoryEventOfCameraPage,
  );

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    dispatch(updateRecoveryHistoryEventOfCameraPage({ camera: null }));
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    dispatch(updateRecoveryHistoryEventOfCameraPage({ camera: null }));
    setIsModalVisible(false);
  };

  const handleOkService = () => {
    setIsModalVisibleService(false);
    dispatch(updateRecoveryCameraPage({ cameraToService: null }));
  };

  const handleCancelService = () => {
    setIsModalVisibleService(false);
    dispatch(updateRecoveryCameraPage({ cameraToService: null }));
  };

  const handleToggleDetail = (detail) => {
    setVisibleDetail(detail);
    dispatch(updateRecoveryCameraPage({ visibleCameraDetail: detail }));
  };

  useEffect(() => {
    if (
      recoveryVisibleCameraDetail &&
      recoveryVisibleCameraDetail !== visibleDetail
    ) {
      setVisibleDetail(recoveryVisibleCameraDetail);
    }
  }, [recoveryVisibleCameraDetail, visibleDetail]);

  useEffect(() => {
    if (
      recoveryCameraToHistoryEvent &&
      recoveryCameraToHistoryEvent.serial === camera?.serial &&
      !isModalVisible
    ) {
      setDataCameraEvent(recoveryCameraToHistoryEvent);
      showModal();
    }
  }, [recoveryCameraToHistoryEvent, camera]);

  useEffect(() => {
    if (
      recoveryCameraToService &&
      recoveryCameraToService.serial === camera?.serial &&
      !isModalVisible
    ) {
      setIsModalVisibleService(true);
      handleToggleDetail(null);
      setDataServiceCamera(camera);
    }
  }, [recoveryCameraToService, camera]);

  const editButton = () => {
    if (!camera) {
      if (!allowRemove) {
        return null;
      }

      return (
        <Tooltip title={removeTooltip}>
          <DeviceNodeAction onClick={onRemove}>
            <TrashCanIcon width={16} height={16} />
          </DeviceNodeAction>
        </Tooltip>
      );
    }

    return (
      <CameraSettingPopup
        camera={camera}
        isVisible={camera?.serial === visibleDetail}
        setVisibleDetail={handleToggleDetail}
        owner={owner}
        handleUpdateStream={onUpdateStream}
        onClickEditButton={() => {
          handleToggleDetail(null);
          // handleUpdateCamera(camera);
          onUpdate(camera);
        }}
        onClickHistoryEvent={() => {
          beeXLogger.debug('Show history event for camera', camera);
          showModal();
          handleToggleDetail(null);
          setDataCameraEvent(camera);
          dispatch(updateRecoveryHistoryEventOfCameraPage({ camera }));
        }}
        onClickServiceDetail={() => {
          setIsModalVisibleService(true);
          handleToggleDetail(null);
          setDataServiceCamera(camera);
          dispatch(updateRecoveryCameraPage({ cameraToService: camera }));
        }}
        onClickDeleteButton={() => {
          handleToggleDetail(null);
          onRemove();
        }}
        allowRemove={variant === 'all'}
      >
        <DeviceNodeAction
          active={camera?.serial === visibleDetail}
          onClick={() => handleToggleDetail(camera?.serial)}
        >
          <SettingsIcon width={16} height={16} />
        </DeviceNodeAction>
      </CameraSettingPopup>
    );
  };

  return (
    <>
      <DeviceNodeWrapper
        level={level}
        isInactive={(state || 0) === 0}
        tooltip={tooltip}
        isSelected={isSelected}
      >
        <DeviceNodeIcon>
          <img src={state !== 1 ? CameraOffIcon : CameraOnIcon} alt="" />
        </DeviceNodeIcon>
        <DeviceNodeName onClick={() => onSelect?.()}>{name}</DeviceNodeName>
        <DeviceNodeActions>
          <Tooltip title="Edit">{editButton()}</Tooltip>
          {variant === 'group' && allowRemove && (
            <Tooltip title={removeTooltip}>
              <DeviceNodeAction
                onClick={onRemove}
                style={{ color: 'rgb(245, 47, 57)' }}
              >
                <MinusCircleFilled />
              </DeviceNodeAction>
            </Tooltip>
          )}
          <DeviceNodeAction onClick={() => onPin?.(!pinned)}>
            {pinned ? <PushpinFilled /> : <PushpinOutlined />}
          </DeviceNodeAction>
        </DeviceNodeActions>
      </DeviceNodeWrapper>

      <HistoryEventsModal
        title={dataCameraEvent?.name}
        data={dataCameraEvent}
        isModalVisible={isModalVisible}
        handleOk={handleOk}
        handleCancel={handleCancel}
        variantPage="camera"
      />
      <ServicesModal
        title={dataServiceCamera?.name}
        isVisible={isModalVisibleService}
        handleOk={handleOkService}
        handleCancel={handleCancelService}
        data={dataServiceCamera}
      />
    </>
  );
};

export const WithLatestStateDeviceNode = ({
  level,
  state,
  name,
  tooltip,
  onSelect,
  isSelected,
  serial,
  allowRemove,
  onRemove,
  removeTooltip,
  camera,
  onUpdate,
  onUpdateStream,
  variant,
  pinned,
  onPin,
}) => {
  const latestState = useLatestCameraState({ serial, defaultState: state });

  return (
    <DeviceNode
      level={level}
      state={latestState}
      name={name}
      tooltip={tooltip}
      onSelect={onSelect}
      isSelected={isSelected}
      allowRemove={allowRemove}
      onRemove={onRemove}
      removeTooltip={removeTooltip}
      onUpdate={onUpdate}
      onUpdateStream={onUpdateStream}
      camera={camera}
      variant={variant}
      pinned={pinned}
      onPin={onPin}
    />
  );
};
